import { Check, Circle, Clock } from "lucide-react";

/**
 * Iter53 · Compact 4-stage match timeline for fixture cards (Iter89 dropped
 * the Fielding gate so teams can go straight from kickoff → wheel).
 *
 * Stages (left→right):
 *   1. Kickoff locked   — scheduled_at set AND no pending reschedule
 *   2. Wheel rolled     — wheelspin_map set OR map is not the Wheelspin placeholder
 *   3. Coin flipped     — coin_toss_winner_team_id set OR faction_home/faction_away chosen
 *   4. Match live       — status is 'live' or 'completed'
 *
 * Colors:
 *   done    · cyan-400
 *   current · #F4D03F (first pending → this is what needs attention)
 *   pending · gray-600
 */
export default function MatchTimeline({ fixture, size = "sm", showLabels = true }) {
  const proposalPending = fixture.reschedule_proposal?.status === "pending";
  const stages = [
    {
      key: "kickoff",
      label: "Kickoff",
      done: !!fixture.scheduled_at && !proposalPending,
      warn: proposalPending ? "reschedule pending" : null,
    },
    {
      key: "wheel",
      label: "Wheel",
      done: !!fixture.wheelspin_map || (fixture.map && String(fixture.map).toLowerCase() !== "wheelspin"),
      skip: fixture.map && String(fixture.map).toLowerCase() !== "wheelspin" && !fixture.wheelspin_map,
    },
    {
      key: "coin",
      label: "Coin",
      done: !!(fixture.coin_toss_winner_team_id || (fixture.faction_home && fixture.faction_away && fixture.coin_flipped_at)),
    },
    {
      key: "live",
      label: "Live",
      done: fixture.status === "live" || fixture.status === "completed",
    },
  ];

  // Mark the first not-done as "current" so reps see what's next.
  let firstPending = -1;
  for (let i = 0; i < stages.length; i++) {
    if (!stages[i].done) { firstPending = i; break; }
  }

  const dot = size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3";
  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      data-testid={`match-timeline-${fixture.id}`}
      aria-label="Match readiness timeline"
    >
      {stages.map((s, i) => {
        const isCurrent = i === firstPending;
        const isDone = s.done;
        const cls = isDone
          ? "border-cyan-400 bg-cyan-400/20 text-cyan-400"
          : isCurrent
            ? "border-[#F4D03F] bg-[#F4D03F]/15 text-[#F4D03F]"
            : "border-[#232330] bg-transparent text-gray-600";
        return (
          <div
            key={s.key}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono uppercase tracking-widest ${cls}`}
            data-testid={`timeline-stage-${s.key}-${isDone ? "done" : isCurrent ? "current" : "pending"}`}
            title={s.warn || `${s.label} · ${isDone ? "done" : isCurrent ? "next up" : "waiting"}`}
          >
            {isDone
              ? <Check className={iconSize} />
              : isCurrent
                ? <Clock className={iconSize} />
                : <Circle className={dot} strokeWidth={2} />}
            {showLabels && <span>{s.label}</span>}
          </div>
        );
      })}
    </div>
  );
}
